/*----------------------------------
- HOME: DEMO SEARCH FIELD
----------------------------------*/

/**
 * The prompt field of the demo's Browse frame (`.bw-prompt`): its placeholder walks through the four shipped examples
 * (`LANDING_HERO_EXAMPLES`), and Enter opens sign-up carrying what the visitor typed as the Product search's prompt, so
 * the search they described is the one they land on once signed in.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule No new trigger id (the conversion path is frozen): Enter fires Home's preview id `website_landing_see_all`, the
 *       one whose destination is the whole search, labelled `prompt_search` so the funnel can tell the two apart.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useState } from 'preact/hooks';

import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import { Field } from '@uniquedomains/components/kit/field/Field';

import Icon from '@/client/components/ui/icon/Icon';
import { HOME_BROWSE_COPY } from '@/client/catalogs/landing/home/homeCopy';
import { LANDING_HERO_EXAMPLES } from '@/client/catalogs/landing/pageContent';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { HOME_DOMAINS_PREVIEW_PAGE, HOME_DOMAINS_PREVIEW_SECTION } from '@/client/pages/landing/lib/useHomeDomainsPreviewHandoff';
import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { toAbsoluteProductUrl } from '@/client/utils/payment/productUrls';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** One example every 3.2s: long enough to read the longest one, short enough to show the range in a visit. */
const PLACEHOLDER_STEP_MS = 3200;

/*----------------------------------
- HELPERS
----------------------------------*/

const buildPromptSignupHref = (prompt: string): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        returnTo: toAbsoluteProductUrl(encodeRadarFiltersUrl({ prompt }, { surface: 'product' })),
        feature: 'databaseAccess',
        action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
        from: 'website_landing_see_all',
    });

/*----------------------------------
- HOOKS
----------------------------------*/

/** The example shown as the placeholder; SSR renders the first, and a hidden tab never advances. */
const useRotatingExample = (): string => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const timer = window.setInterval(() => {
            if (!document.hidden) setIndex((current) => (current + 1) % LANDING_HERO_EXAMPLES.length);
        }, PLACEHOLDER_STEP_MS);
        return () => window.clearInterval(timer);
    }, []);
    return LANDING_HERO_EXAMPLES[index];
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeSearchField = () => {
    const [prompt, setPrompt] = useState('');
    const placeholder = useRotatingExample();

    const submit = () => {
        const typed = prompt.trim();
        if (!typed) return;
        const href = buildPromptSignupHref(typed);
        trackLandingCta({ page: HOME_DOMAINS_PREVIEW_PAGE, section: HOME_DOMAINS_PREVIEW_SECTION, label: 'prompt_search', kind: 'product', href, surface: 'results' });
        trackConversionTrigger('website_landing_see_all', {
            action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
            feature: 'databaseAccess',
            href,
            page: HOME_DOMAINS_PREVIEW_PAGE,
            section: HOME_DOMAINS_PREVIEW_SECTION,
        });
        openWebsiteAuthHref(href);
    };

    return (
        <Field
            icon={<Icon name="search" size={16} />}
            value={prompt}
            onInput={setPrompt}
            placeholder={placeholder}
            label={HOME_BROWSE_COPY.searchLabel}
            data-testid="home-demo-search"
            onKeyDown={(event: KeyboardEvent) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                submit();
            }}
        />
    );
};

export default HomeSearchField;
